import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DocumentArrowDownIcon,
  CalendarIcon,
  UserCircleIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';

import { showSuccess, showError, showConfirm } from '../../utils/notificationHelper';
import { disputesAPI } from '../../services/api';
import FileViewerModal from '../common/FileViewerModal';

// Content with View More/Less component and custom tooltip
const ContentWithEllipsis = ({ content, maxLength = 100 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Ensure content is always a string to prevent "length of undefined" errors
  const safeContent = content || '';

  // Only use ellipsis when content exceeds maxLength
  const shouldTruncate = safeContent.length > maxLength;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      {shouldTruncate ? (
        <div className="relative group">
          {/* Main text with truncation when needed */}
          <div className="text-base text-gray-700 whitespace-pre-line leading-relaxed">
            {!isExpanded ? `${safeContent.substring(0, maxLength)}...` : safeContent}
          </div>

          {/* Show More/Less button */}
          <button
            onClick={toggleExpand}
            className="mt-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-200 flex items-center"
          >
            {isExpanded ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Show Less
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Show More
              </>
            )}
          </button>
        </div>
      ) : (
        // If content is short, just display it normally without any truncation
        <p className="text-base text-gray-700 whitespace-pre-line leading-relaxed">
          {safeContent}
        </p>
      )}
    </div>
  );
};

const MinutesTabContent = ({ dispute, onRefresh }) => {
  // State management with optimized performance
  const [notes, setNotes] = useState(dispute?.notes || []);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [contentLoading, setContentLoading] = useState(false); // Track loading state for parent content
  const [showAddNotesModal, setShowAddNotesModal] = useState(false);
  const [showConfirmDeleteAttachment, setShowConfirmDeleteAttachment] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [notesFormData, setNotesFormData] = useState({
    note_type: 'meeting_minutes',
    content: '',
    meeting_date: new Date().toISOString().split('T')[0],
    attachment_file: null,
    attachments: [] // For multiple files
  });
  // Separate state for edit mode to avoid conflicts
  const [currentFormData, setCurrentFormData] = useState({
    note_type: 'meeting_minutes',
    content: '',
    meeting_date: new Date().toISOString().split('T')[0],
    attachment_file: null,
    attachment_url: null,
    attachments: [], // For multiple files
    attachment_urls: [] // For displaying existing attachments
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [editNoteData, setEditNoteData] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [viewAttachment, setViewAttachment] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Refs for improved DOM interaction
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);
  const multipleFileInputRef = useRef(null);

  // Helper function to update the parent container in a consistent way
  // This function ensures fresh data is fetched from the database after any operation
  const updateParentContainer = useCallback(async (forceTrigger = false) => {
    console.log('updateParentContainer: Fetching fresh data from database', forceTrigger ? '(FORCED)' : '');

    if (!onRefresh || typeof onRefresh !== 'function') {
      console.error('updateParentContainer: No parent refresh function available');
      return { success: false, reason: 'no-function' };
    }

    // Simplified direct approach - always fetches fresh data from database
    try {
      console.log('updateParentContainer: Calling parent onRefresh to get fresh database data');

      // Direct parent refresh call that will fetch new data from database
      const refreshResult = await onRefresh({forceRefresh: true, fetchFromDatabase: true});
      console.log('Parent refresh completed with fresh database data', refreshResult ? 'successfully' : 'without data');

      // If we received data with notes, update our local state
      if (refreshResult && refreshResult.notes) {
        // Sort notes by created_at date descending (newest first)
        const sortedNotes = [...refreshResult.notes].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );

        // Update main notes array directly
        setNotes(sortedNotes);
        console.log('Updated notes from fresh database data:', sortedNotes.length);

        // Also update filtered notes to match current filter
        const filtered = sortedNotes.filter(note => {
          if (activeFilter === 'all') return true;
          if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
          if (activeFilter === 'case') return note.note_type === 'case_note';
          if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
          return true;
        });

        setFilteredNotes(filtered);
        console.log('Updated filtered notes from fresh database data:', filtered.length);
      }

      return { success: true, data: refreshResult };
    } catch (error) {
      console.error('Error during parent refresh:', error);
      // Force a local refresh as fallback
      setRefreshTrigger(prev => prev + 1);
      return { success: false, reason: 'error', error };
    }
  }, [onRefresh, activeFilter]);

  // Define the loadNotes function first - IMPORTANT: functions must be defined before they're used
  // Always fetches fresh data from the database to ensure we have the latest notes
  const loadNotes = useCallback(async () => {
    console.log('LoadNotes called - retrieving latest notes from database');

    // Set content loading state to true
    setContentLoading(true);

    try {
      // First try to get fresh data from the database via parent's refresh function
      if (typeof onRefresh === 'function') {
        console.log('Requesting fresh data from database via parent refresh');

        // Request fresh data with special flags
        const freshData = await onRefresh({
          forceRefresh: true,
          fetchFromDatabase: true,
          operation: 'load'
        });

        if (freshData && freshData.notes) {
          console.log('Received fresh database data with', freshData.notes.length, 'notes');

          // Sort notes by created_at date descending (newest first)
          const sortedNotes = [...freshData.notes].sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );

          // Check if attachments are present in the data
          const hasAttachments = sortedNotes.some(note =>
            note.attachment_url || (Array.isArray(note.attachment_urls) && note.attachment_urls.length > 0)
          );

          console.log('Fresh notes sorted, setting state with', sortedNotes.length, 'notes',
            hasAttachments ? '(attachments found)' : '(no attachments found)');

          // Update state with fresh data
          setNotes(sortedNotes);

          // Update filtered notes based on current filter
          const filtered = sortedNotes.filter(note => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
            if (activeFilter === 'case') return note.note_type === 'case_note';
            if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
            return true;
          });

          setFilteredNotes(filtered);
          return; // Exit early since we got fresh data
        }
      }

      // Fallback: If we couldn't get fresh data or onRefresh isn't available,
      // use the data from the parent component
      if (dispute && dispute.notes) {
        console.log('Using notes from parent dispute object as fallback:', dispute.notes.length);

        // Sort notes by created_at date descending (newest first)
        const sortedNotes = [...dispute.notes].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );

        // Check if attachments are present in the data
        const hasAttachments = sortedNotes.some(note =>
          note.attachment_url || (Array.isArray(note.attachment_urls) && note.attachment_urls.length > 0)
        );

        console.log('Fallback notes sorted, setting state with', sortedNotes.length, 'notes',
          hasAttachments ? '(attachments found)' : '(no attachments found)');

        setNotes(sortedNotes);
      } else {
        console.warn('No notes data available from any source');
      }
    } catch (error) {
      console.error('Error loading notes:', error);
      showError('Failed to load the latest notes. Please refresh the page.');
    } finally {
      // Always set loading state to false when done, whether successful or not
      setContentLoading(false);
    }
  }, [dispute, onRefresh]);

  // Synchronize with parent container - ensures both component and parent have consistent data
  // Always fetches fresh data from database
  const syncWithParentContainer = useCallback(async (forceTrigger = false) => {
    // Skip if we're already in the middle of a refresh (unless forced)
    if (!forceTrigger && window._isCurrentlyRefreshing) {
      console.log('syncWithParentContainer: Skipping because refresh is already in progress');
      return;
    }

    console.log('syncWithParentContainer called - fetching fresh data from database');

    // Set a flag to indicate we're refreshing
    window._isCurrentlyRefreshing = true;
    const refreshStartTime = Date.now();

    try {
      // First call parent's onRefresh to update data at the parent level
      if (typeof onRefresh === 'function') {
        try {
          console.log('syncWithParentContainer: Calling parent onRefresh function with database fetch flags');

          // Create a timeout promise that will reject after 3 seconds
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Parent refresh timed out')), 3000);
          });

          // Race the parent refresh against the timeout, requesting fresh data from database
          const refreshedData = await Promise.race([
            Promise.resolve(onRefresh({
              forceRefresh: true,
              fetchFromDatabase: true,
              operation: 'sync'
            })),
            timeoutPromise
          ]);

          console.log('syncWithParentContainer: Parent container updated successfully');

          // If we got updated data directly, use it to update our notes
          if (refreshedData && refreshedData.notes) {
            const sortedNotes = [...refreshedData.notes].sort((a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
            );

            // Update our notes without triggering another refresh
            setNotes(sortedNotes);
            console.log('syncWithParentContainer: Notes updated from parent refresh:', sortedNotes.length);

            // Update filtered notes based on the new data and current filter
            const filtered = sortedNotes.filter(note => {
              if (activeFilter === 'all') return true;
              if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
              if (activeFilter === 'case') return note.note_type === 'case_note';
              if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
              return true;
            });

            setFilteredNotes(filtered);
            console.log('syncWithParentContainer: Filtered notes updated:', filtered.length);

            return { success: true, data: refreshedData }; // Return success status and data
          } else {
            console.log('syncWithParentContainer: Parent refresh did not return expected data structure');
            // Even though we didn't get data, trigger a refresh in case parent state was updated
            setRefreshTrigger(prev => prev + 1);
            return { success: false, reason: 'no-data' };
          }
        } catch (error) {
          console.error('syncWithParentContainer: Error in parent refresh:', error);

          // If there was an error or timeout, trigger our own refresh mechanism as fallback
          console.log('syncWithParentContainer: Using fallback refresh mechanism');
          setRefreshTrigger(prev => prev + 1);

          return { success: false, reason: 'error', error };
        }
      } else {
        console.log('syncWithParentContainer: No parent onRefresh function available');
        // Even without onRefresh, still refresh our own state
        setRefreshTrigger(prev => prev + 1);
        return { success: false, reason: 'no-function' };
      }
    } finally {
      // Reset refresh flag after a delay to prevent immediate re-triggering
      setTimeout(() => {
        window._isCurrentlyRefreshing = false;
        console.log(`syncWithParentContainer: Completed in ${Date.now() - refreshStartTime}ms`);
      }, 500);
    }
  }, [onRefresh, activeFilter]);

  // Emergency direct parent refresh - use only as a last resort
  // This bypasses all our safeguards and directly calls the parent's refresh
  const emergencyParentRefresh = useCallback(async () => {
    console.log('⚠️ EMERGENCY: Direct parent refresh called');
    if (typeof onRefresh === 'function') {
      try {
        // Directly call the parent refresh with no checks
        const result = await Promise.resolve(onRefresh());
        console.log('Emergency parent refresh completed', result ? 'successfully' : 'with no data');
        return { success: true, data: result };
      } catch (error) {
        console.error('Emergency parent refresh failed:', error);
        return { success: false, error };
      }
    } else {
      console.error('Emergency parent refresh failed: No onRefresh function available');
      return { success: false, reason: 'no-function' };
    }
  }, [onRefresh]);

  // Force refresh notes using parent component's data fetching
  // with protection against infinite loops
  const forceRefresh = useCallback(async () => {
    // First try our standard sync method
    console.log('forceRefresh called - forcing parent container update');
    const result = await syncWithParentContainer(true);

    // If that didn't work, try the emergency refresh
    if (!result || !result.success) {
      console.log('Standard sync failed in forceRefresh, trying emergency refresh');
      return await emergencyParentRefresh();
    }

    return result;
  }, [syncWithParentContainer, emergencyParentRefresh]);

  // Set up component effects

  // Load notes when dispute changes or refresh is triggered - now loadNotes is already defined
  // We use a ref to debounce refresh operations
  const refreshDebounceRef = useRef({
    lastRefreshTime: 0,
    isRefreshing: false,
    refreshCount: 0
  });

  useEffect(() => {
    if (!dispute || !dispute.id) return;

    console.log('Dispute changed or refresh triggered - loading notes',
      { disputeId: dispute.id, notesCount: dispute.notes?.length, refreshTrigger });

    // Debounce frequent refreshes to prevent infinite loops
    const now = Date.now();
    const timeSinceLastRefresh = now - refreshDebounceRef.current.lastRefreshTime;

    if (refreshDebounceRef.current.isRefreshing) {
      console.log('Skipping refresh because another refresh is in progress');
      return;
    }

    if (timeSinceLastRefresh < 1000) {
      refreshDebounceRef.current.refreshCount++;

      if (refreshDebounceRef.current.refreshCount > 3) {
        console.warn('Too many refresh attempts in a short time, skipping to prevent infinite loop');
        return;
      }

      console.log(`Rapid refresh detected: ${timeSinceLastRefresh}ms since last refresh`);
    } else {
      // Reset counter if it's been a while
      refreshDebounceRef.current.refreshCount = 0;
    }

    // Update refresh time
    refreshDebounceRef.current.lastRefreshTime = now;

    // Mark that we're refreshing
    refreshDebounceRef.current.isRefreshing = true;

    // Always call loadNotes first for consistent state management
    loadNotes().finally(() => {
      // Mark refresh as complete
      setTimeout(() => {
        refreshDebounceRef.current.isRefreshing = false;
      }, 500);
    });

    // We no longer need to make additional calls when refreshTrigger changes
    // The loadNotes function will handle everything
  }, [dispute, refreshTrigger, loadNotes]);

  // Extra check to update notes immediately when dispute.notes changes
  // We use a ref to track if we need to update to avoid excessive rerenders
  const notesUpdateRef = useRef({
    lastUpdate: 0,
    lastCount: 0,
    lastHash: '' // Add a hash to track content changes, not just count
  });

  useEffect(() => {
    // Skip if we're in the middle of a refresh operation
    if (window._isCurrentlyRefreshing) {
      console.log('Skipping notes update during refresh operation');
      return;
    }

    if (dispute && dispute.notes && dispute.notes.length > 0) {
      // Generate a simple hash of the notes array for change detection
      // This helps detect when content changes even if count remains the same
      const generateSimpleHash = (notes) => {
        return notes.map(n => `${n.id}-${n.updated_at}`).join('|');
      };

      // Check if this is a meaningful update by comparing count, content hash, and last update time
      const now = Date.now();
      const noteCount = dispute.notes.length;
      const currentHash = generateSimpleHash(dispute.notes);
      const timeSinceLastUpdate = now - notesUpdateRef.current.lastUpdate;
      const hashChanged = currentHash !== notesUpdateRef.current.lastHash;

      // Log what we're detecting for debugging
      console.log('Parent notes update check:', {
        noteCount,
        previousCount: notesUpdateRef.current.lastCount,
        hashChanged,
        timeSinceLastUpdate
      });

      // Update if count changed, content hash changed, or it's been at least 2 seconds since last update
      if (noteCount !== notesUpdateRef.current.lastCount || hashChanged || timeSinceLastUpdate > 2000) {
        const sortedNotes = [...dispute.notes].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );

        console.log('Notes updated directly from parent container:', sortedNotes.length,
                    'previous count:', notesUpdateRef.current.lastCount,
                    'hash changed:', hashChanged,
                    'time since last update:', timeSinceLastUpdate);

        // Update our reference values
        notesUpdateRef.current = {
          lastUpdate: now,
          lastCount: noteCount,
          lastHash: currentHash
        };

        // Update state
        setNotes(sortedNotes);

        // Also update filtered notes to stay in sync with main notes
        const filtered = sortedNotes.filter(note => {
          if (activeFilter === 'all') return true;
          if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
          if (activeFilter === 'case') return note.note_type === 'case_note';
          if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
          return true;
        });

        console.log(`Also updating filtered notes with ${filtered.length} notes`);
        setFilteredNotes(filtered);
      } else {
        console.log('Skipping redundant notes update, no meaningful changes detected');
      }
    }
  }, [dispute?.notes, activeFilter]);

  // Filter notes when notes array changes or filter changes
  useEffect(() => {
    console.log('Filtering notes based on activeFilter:', activeFilter);

    // Create a function to filter notes by type
    const filterNotesByType = () => {
      if (activeFilter === 'all') {
        return notes;
      } else if (activeFilter === 'minutes') {
        return notes.filter(note => note.note_type === 'meeting_minutes');
      } else if (activeFilter === 'case') {
        return notes.filter(note => note.note_type === 'case_note');
      } else if (activeFilter === 'resolution') {
        return notes.filter(note => note.note_type === 'resolution_note');
      }
      return notes; // Default fallback
    };

    // Apply the filtering immediately
    const filtered = filterNotesByType();
    console.log(`Filtered notes: ${filtered.length} (from total ${notes.length})`);

    // Update state with filtered results
    setFilteredNotes(filtered);

  }, [notes, activeFilter]);

  // Note management functions

  // Handle note edit
  const handleEditNote = (note) => {
    // Set the edit data and reset any previous errors
    setEditNoteData(note);
    setErrors({});

    console.log('Editing note:', note); // Debug log to see what we're getting

    // Format the meeting date properly for the input field (YYYY-MM-DD)
    let formattedDate;
    if (note.meeting_date) {
      // Handle different ISO date formats that might come from the API
      // This regex extracts just the YYYY-MM-DD part from any ISO date string format
      const dateMatch = note.meeting_date.match(/^(\d{4}-\d{2}-\d{2})/);
      formattedDate = dateMatch ? dateMatch[1] : new Date().toISOString().split('T')[0];
    } else {
      formattedDate = new Date().toISOString().split('T')[0];
    }

    // Ensure we have a valid note type, defaulting to meeting_minutes if missing
    const noteType = ['meeting_minutes', 'case_note', 'resolution_note'].includes(note.note_type)
                     ? note.note_type
                     : 'meeting_minutes';

    console.log('Using note type:', noteType); // Debug log for note type
    console.log('Attachment URL:', note.attachment_url); // Debug log for attachment

    // Set currentFormData with valid default values to avoid validation errors
    setCurrentFormData({
      note_type: noteType,
      content: note.content || '',
      meeting_date: formattedDate,
      attachment_file: null, // Will be set when user uploads a new file
      attachment_url: note.attachment_url || null,
      // Store the original note data for reference
      original: {
        note_type: note.note_type,
        content: note.content,
        meeting_date: note.meeting_date
      }
    });

    console.log('Form data after setup:', { noteType, content: note.content, formattedDate });
    // No need to set notesFormData in edit mode, we're using currentFormData

    // After setting up the data, show the modal
    setShowAddNotesModal(true);

    // Additional validation safeguard - ensure we have a selected type
    setTimeout(() => {
      console.log('Setting up radio buttons for note_type:', noteType);

      // First, clear any existing selections to avoid multiple selections
      document.querySelectorAll('input[name="note_type"]').forEach(radio => {
        radio.checked = false;
        radio.removeAttribute('data-selected');
      });

      // Now set the correct radio button
      const radioInput = document.querySelector(`input[value="${noteType}"]`);
      if (radioInput) {
        radioInput.checked = true;
        radioInput.setAttribute('data-selected', 'true');
        console.log('Selected radio button for:', noteType);
      } else {
        console.warn('Could not find radio button for note type:', noteType);
      }

      // Double check that the form data is properly set
      if (editNoteData) {
        console.log('Edit mode form data after setup:', currentFormData);
      } else {
        console.log('Add mode form data after setup:', notesFormData);
      }
    }, 100);
  };

  // Handle note delete with confirmation
  const handleDeleteNote = async (noteId) => {
    // Close the confirmation modal immediately for better UX
    setShowConfirmDelete(false);
    setNoteToDelete(null);

    setLoading(true);
    try {
      // STEP 1: Call API to delete note
      console.log('Deleting note with ID:', noteId);
      await disputesAPI.deleteNote(dispute.id, noteId);
      showSuccess('Note deleted successfully');

      // STEP 2: Update local state immediately for responsive UI
      const deletedNoteId = noteId; // Keep a reference to the deleted note ID

      // Update main notes array
      setNotes(prevNotes => {
        const updatedNotes = prevNotes.filter(note => note.id !== deletedNoteId);
        console.log(`Updated notes after deletion: ${updatedNotes.length} notes`);
        return updatedNotes;
      });

      // Update filtered notes as well
      setFilteredNotes(prevFiltered => {
        const updatedFiltered = prevFiltered.filter(note => note.id !== deletedNoteId);
        console.log(`Updated filtered notes after deletion: ${updatedFiltered.length} notes`);
        return updatedFiltered;
      });

      // STEP 3: Get fresh data from database and update parent
      console.log('STEP 3: Fetching fresh data from database after deletion');
      if (onRefresh && typeof onRefresh === 'function') {
        try {
          // Request fresh data from database with special flags
          const freshData = await onRefresh({
            forceRefresh: true,
            fetchFromDatabase: true,
            operation: 'delete',
            noteId: deletedNoteId
          });
          console.log('Fresh database data fetched after deletion:',
            freshData && freshData.notes ? freshData.notes.length + ' notes' : 'no notes data');

          // If we got fresh data back, update our state with it
          if (freshData && freshData.notes) {
            // Sort notes by created_at date descending (newest first)
            const sortedNotes = [...freshData.notes].sort((a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
            );

            // Update both main and filtered notes arrays
            setNotes(sortedNotes);

            // Update filtered notes based on current filter
            const filtered = sortedNotes.filter(note => {
              if (activeFilter === 'all') return true;
              if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
              if (activeFilter === 'case') return note.note_type === 'case_note';
              if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
              return true;
            });

            setFilteredNotes(filtered);
          }
        } catch (refreshError) {
          console.error('Error fetching fresh data after deletion:', refreshError);

          // Use our dedicated helper function as backup
          console.log('Using updateParentContainer as backup after failed refresh');
          await updateParentContainer(true);
        }
      } else {
        console.warn('No onRefresh function available, using fallback refresh method');
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      showError('Failed to delete note');

      // Force refresh to ensure UI is in sync with database
      try {
        await updateParentContainer(true);
      } catch (refreshError) {
        console.error('Failed to refresh after error:', refreshError);
      }
    } finally {
      setLoading(false);
      // Modal is already closed at the beginning of the function
    }
  };

  // Handle deleting the main attachment of a note
  const handleDeleteMainAttachment = (noteId) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;

    setAttachmentToDelete({
      noteId: noteId,
      type: 'main',
      attachmentIndex: null,
      name: note.attachment_path ? cleanFileName(note.attachment_path) : 'attachment'
    });

    setShowConfirmDeleteAttachment(true);
  };

  // Handle deleting a specific attachment from the attachment_urls array
  const handleDeleteAttachment = (noteId, attachmentIndex) => {
    const note = notes.find(n => n.id === noteId);
    if (!note || !note.attachment_urls || attachmentIndex >= note.attachment_urls.length) return;

    const attachment = note.attachment_urls[attachmentIndex];

    setAttachmentToDelete({
      noteId: noteId,
      type: 'multiple',
      attachmentIndex: attachmentIndex,
      name: attachment.name ? cleanFileName(attachment.name) : (attachment.url ? cleanFileName(attachment.url) : 'attachment')
    });

    setShowConfirmDeleteAttachment(true);
  };

  // Execute the attachment deletion
  const executeAttachmentDeletion = async () => {
    if (!attachmentToDelete) return;

    // Close the confirmation modal immediately for better UX
    setShowConfirmDeleteAttachment(false);
    const attachmentInfo = {...attachmentToDelete}; // Keep a copy of the data

    setLoading(true);
    console.log('Starting attachment deletion process...');

    try {
      const { noteId, type, attachmentIndex } = attachmentInfo;
      const noteToUpdate = notes.find(n => n.id === noteId);

      console.log('Attachment to delete:', {
        noteId,
        type,
        attachmentIndex,
        found: Boolean(noteToUpdate)
      });

      if (!noteToUpdate) {
        throw new Error('Note not found');
      }

      // STEP 1: Update local UI immediately BEFORE sending to server
      // This ensures the user sees the change right away
      console.log('STEP 1: Updating UI immediately before server request...');

      setNotes(prevNotes => {
        const updatedNotes = prevNotes.map(note => {
          if (note.id !== noteId) return note;

          // Create a deep copy to avoid mutation issues
          const updatedNote = JSON.parse(JSON.stringify(note));

          if (type === 'main') {
            updatedNote.attachment_url = null;
            updatedNote.attachment_path = null;
          } else if (type === 'multiple' && Array.isArray(updatedNote.attachment_urls)) {
            updatedNote.attachment_urls = updatedNote.attachment_urls.filter((_, idx) => idx !== attachmentIndex);
          }

          return updatedNote;
        });

        return updatedNotes;
      });

      // STEP 2: Prepare the form data for server request
      console.log('STEP 2: Preparing form data for server request');
      const updateFormData = new FormData();
      updateFormData.append('note_type', noteToUpdate.note_type || 'case_note');
      updateFormData.append('content', noteToUpdate.content || '');

      if (noteToUpdate.note_type === 'meeting_minutes' && noteToUpdate.meeting_date) {
        updateFormData.append('meeting_date', noteToUpdate.meeting_date.split(' ')[0]);
      }

      if (type === 'main') {
        updateFormData.append('remove_attachment', 'true');
        console.log('Removing main attachment');
      } else if (type === 'multiple' && Array.isArray(noteToUpdate.attachment_urls)) {
        updateFormData.append('keep_attachments', 'true');

        const currentIndexes = Array.from(
          { length: noteToUpdate.attachment_urls.length },
          (_, i) => i
        );

        const indexesToKeep = currentIndexes.filter(idx => idx !== attachmentIndex);

        if (indexesToKeep.length > 0) {
          updateFormData.append('attachment_indexes_to_keep', JSON.stringify(indexesToKeep));
          console.log('Deleting attachment index:', attachmentIndex);
          console.log('Keeping attachment indexes:', indexesToKeep);
        } else {
          updateFormData.append('remove_all_attachments', 'true');
          console.log('Removing all attachments');
        }
      }

      // STEP 3: Send update to server
      console.log('STEP 3: Sending attachment deletion request to server...');
      await disputesAPI.updateNote(dispute.id, noteId, updateFormData);
      console.log('Server update successful');

      // STEP 4: Show success message
      showSuccess('Attachment deleted successfully');

      // STEP 5: Update filtered notes based on the updated notes array
      console.log('STEP 5: Updating filtered notes based on updated main notes');

      // We need to get the latest notes array after our update
      const updatedNotes = notes.map(note => {
        if (note.id !== noteId) return note;

        // Return the updated version from our earlier update
        const updatedNote = {...note};
        if (type === 'main') {
          updatedNote.attachment_url = null;
          updatedNote.attachment_path = null;
        } else if (type === 'multiple' && Array.isArray(updatedNote.attachment_urls)) {
          updatedNote.attachment_urls = updatedNote.attachment_urls.filter((_, idx) => idx !== attachmentIndex);
        }
        return updatedNote;
      });

      // Now filter based on the updated notes
      setFilteredNotes(prevFiltered => {
        // Filter based on the activeFilter and our updated notes
        const filtered = updatedNotes.filter(note => {
          if (activeFilter === 'all') return true;
          if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
          if (activeFilter === 'case') return note.note_type === 'case_note';
          if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
          return true;
        });

        console.log(`Updated filtered notes: ${filtered.length} notes (filter: ${activeFilter})`);
        return filtered;
      });

      // STEP 6: Get fresh data from database after attachment deletion
      console.log('STEP 6: Fetching fresh data from database after attachment deletion');
      if (onRefresh && typeof onRefresh === 'function') {
        try {
          // Request fresh data from database with special flags
          const freshData = await onRefresh({
            forceRefresh: true,
            fetchFromDatabase: true,
            operation: 'deleteAttachment',
            noteId: noteId,
            attachmentType: type,
            attachmentIndex: attachmentIndex
          });

          console.log('Fresh database data fetched after attachment deletion:',
            freshData && freshData.notes ? freshData.notes.length + ' notes' : 'no notes data');

          // If we got fresh data back, update our state with it
          if (freshData && freshData.notes) {
            // Sort notes by created_at date descending (newest first)
            const sortedNotes = [...freshData.notes].sort((a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
            );

            // Update both main and filtered notes arrays
            setNotes(sortedNotes);

            // Update filtered notes based on current filter
            const filtered = sortedNotes.filter(note => {
              if (activeFilter === 'all') return true;
              if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
              if (activeFilter === 'case') return note.note_type === 'case_note';
              if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
              return true;
            });

            setFilteredNotes(filtered);
            console.log('State updated with fresh database data after attachment deletion');
          }
        } catch (refreshError) {
          console.error('Error fetching fresh data after attachment deletion:', refreshError);

          // Use our dedicated helper function as backup
          console.log('Using updateParentContainer as backup after failed refresh');
          await updateParentContainer(true);
        }
      } else {
        console.warn('No onRefresh function available, using backup refresh method');
        // Use backup method to force refresh
        await updateParentContainer(true);
      }

    } catch (error) {
      console.error('Error deleting attachment:', error);

      // Show error message to user
      showError('Failed to delete attachment. Please try again.');

      // Try to restore the previous state if UI was already updated
      if (attachmentToDelete) {
        console.log('Attempting to restore original notes state after error');
        // Force a refresh to restore the correct state
        setRefreshTrigger(prev => prev + 1);
      }
    } finally {
      // Just clear loading state, modal is already closed at the beginning of the function
      setLoading(false);
      setAttachmentToDelete(null);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      // Parse time string (HH:MM:SS) to display in 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert to 12-hour format
      return `${formattedHour}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString; // Return original if parsing fails
    }
  };

  const getNoteTypeBadgeClass = (type) => {
    const typeClasses = {
      'meeting_minutes': 'bg-violet-200 text-violet-800',
      'case_note': 'bg-blue-100 text-blue-800',
      'resolution_note': 'bg-green-100 text-green-800',
      'default': 'bg-gray-100 text-gray-800'
    };
    return `px-2 py-0.5 rounded-full text-xs font-medium ${typeClasses[type] || typeClasses.default}`;
  };

  const getNoteTypeLabel = (type) => {
    const typeLabels = {
      'meeting_minutes': 'Meeting Minutes',
      'case_note': 'Case Note',
      'resolution_note': 'Resolution Note',
      'default': 'Note'
    };
    return typeLabels[type] || typeLabels.default;
  };

  // Clean filename by removing prefixes (like timestamps, uuids, etc.)
  const cleanFileName = (fileName) => {
    if (!fileName) return 'attachment';

    // First get the filename without the path
    const fileNameOnly = fileName.split('/').pop();

    // Remove common prefixes like:
    // - Timestamps (e.g., 20230817_123456_)
    // - UUIDs (e.g., 550e8400-e29b-41d4-a716-446655440000_)
    // - Other system generated prefixes

    // Pattern 1: Remove timestamp patterns like YYYYMMDD_HHMMSS_ or YYYY-MM-DD_
    let cleanedName = fileNameOnly.replace(/^\d{8}_\d{6}_/, '')
                                 .replace(/^\d{4}-\d{2}-\d{2}_/, '');

    // Pattern 2: Remove UUID-like prefixes
    cleanedName = cleanedName.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/, '');

    // Pattern 3: Remove timestamp with milliseconds like 1629380425983_
    cleanedName = cleanedName.replace(/^\d{13}_/, '');

    // Pattern 4: Remove numeric prefixes followed by underscore or dash
    cleanedName = cleanedName.replace(/^\d+[_-]/, '');

    return cleanedName;
  };

  // Handle single file change (legacy support)
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log('Selected file:', file.name, file.type, file.size);

      // Validate file type immediately
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      if (!allowedTypes.includes(file.type)) {
        setErrors({
          ...errors,
          attachment_file: 'Only image, PDF, and document files are allowed'
        });
        // Clear the file input
        e.target.value = '';
        return;
      }

      // File size validation (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({
          ...errors,
          attachment_file: 'File size cannot exceed 5MB'
        });
        // Clear the file input
        e.target.value = '';
        return;
      }

      // Clear any previous file errors
      if (errors.attachment_file) {
        setErrors({
          ...errors,
          attachment_file: undefined
        });
      }

      // Handle the update depending on whether we're in edit mode or add mode
      if (editNoteData) {
        console.log('Edit mode: Setting file in currentFormData:', file.name);
        setCurrentFormData(prevData => {
          const updatedData = {
            ...prevData,
            attachment_file: file,
            attachment_url: URL.createObjectURL(file) // Preview URL
          };
          console.log('Updated currentFormData with file:', updatedData.attachment_file.name);
          return updatedData;
        });
      } else {
        console.log('Add mode: Setting file in notesFormData:', file.name);
        setNotesFormData(prevData => {
          const updatedData = {
            ...prevData,
            attachment_file: file
          };
          console.log('Updated notesFormData with file:', updatedData.attachment_file.name);
          return updatedData;
        });
      }
    }
  };

  // Handle multiple file uploads
  const handleMultipleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileList = Array.from(e.target.files);
      console.log('Selected multiple files:', fileList.map(f => f.name));

      // Validate files
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      // Check for invalid files
      const invalidFiles = fileList.filter(file => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        setErrors({
          ...errors,
          attachments: `Some files are not allowed. Only images, PDF, and document files are accepted.`
        });
        e.target.value = '';
        return;
      }

      // Check for oversized files
      const oversizedFiles = fileList.filter(file => file.size > 5 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        setErrors({
          ...errors,
          attachments: `Some files exceed the 5MB size limit.`
        });
        e.target.value = '';
        return;
      }

      // Clear any previous errors
      if (errors.attachments) {
        setErrors({
          ...errors,
          attachments: undefined
        });
      }

      // Handle update based on mode
      if (editNoteData) {
        console.log('Edit mode: Setting multiple files in currentFormData');
        setCurrentFormData(prevData => {
          // Create URL objects for previewing files
          const fileUrls = fileList.map(file => ({
            file,
            url: URL.createObjectURL(file),
            name: file.name,
            size: file.size,
            type: file.type
          }));

          return {
            ...prevData,
            attachments: fileList,
            attachment_urls: [...(prevData.attachment_urls || []), ...fileUrls]
          };
        });
      } else {
        console.log('Add mode: Setting multiple files in notesFormData');
        setNotesFormData(prevData => {
          return {
            ...prevData,
            attachments: fileList
          };
        });
      }
    }
  };  // Handle view attachment in modal
  const handleViewAttachment = (attachment) => {
    setViewAttachment(attachment);
  };

  // Close attachment viewer
  const closeAttachmentViewer = () => {
    setViewAttachment(null);
  };

  // Reset form to initial state (synchronous, no data refresh)
  const resetForm = () => {
    // Close the modal first
    setShowAddNotesModal(false);

    // Reset form state
    setNotesFormData({
      note_type: 'meeting_minutes',
      content: '',
      meeting_date: new Date().toISOString().split('T')[0],
      attachment_file: null,
      attachments: []
    });

    // Reset edit form data
    setCurrentFormData({
      note_type: 'meeting_minutes',
      content: '',
      meeting_date: new Date().toISOString().split('T')[0],
      attachment_file: null,
      attachment_url: null,
      attachments: [],
      attachment_urls: []
    });

    setEditNoteData(null);
    setErrors({});

    // Reset any DOM-level form state (like radio buttons)
    setTimeout(() => {
      // Reset radio buttons to ensure proper UI state
      document.querySelectorAll('input[name="note_type"]').forEach(radio => {
        radio.checked = false;
      });

      // Set the default radio button
      const defaultRadio = document.querySelector('input[value="meeting_minutes"]');
      if (defaultRadio) {
        defaultRadio.checked = true;
      }
    }, 0);

    // Ensure all radio buttons are reset properly on the next render cycle
    setTimeout(() => {
      // Clear any existing checked states
      document.querySelectorAll('input[name="note_type"]').forEach(radio => {
        radio.checked = false;
        radio.removeAttribute('data-selected');
      });

      // Set the default radio button
      const defaultRadio = document.querySelector('input[value="meeting_minutes"]');
      if (defaultRadio) {
        defaultRadio.checked = true;
        defaultRadio.setAttribute('data-selected', 'true');
      }
    }, 50);
  };

  // Optimized form submission with local state updates
  const submitNotes = async (e) => {
    e.preventDefault();
    setErrors({});

    // First, try to capture the current state of form data from form elements
    // This is a fallback mechanism to ensure we have the latest values

    // For note_type, get the value more reliably with explicit logging
    const selectedRadio = document.querySelector('input[name="note_type"]:checked');
    console.log('Selected radio button:', selectedRadio);
    console.log('Selected note type value:', selectedRadio?.value);

    const formElements = {
      note_type: selectedRadio?.value || (editNoteData ? currentFormData.note_type : notesFormData.note_type) || 'meeting_minutes',
      content: document.getElementById('content')?.value,
      meeting_date: document.getElementById('meeting_date')?.value
    };

    // Log state of file input for debugging
    const fileInput = document.getElementById('attachment_file');
    if (fileInput && fileInput.files && fileInput.files[0]) {
      console.log('File input has file:', fileInput.files[0].name);
    } else {
      console.log('No file in file input element');
    }

    console.log('Form element values:', formElements);

    // Get the form data from the appropriate state based on mode
    let formData;
    if (editNoteData) {
      formData = {
        ...currentFormData,
        note_type: formElements.note_type || currentFormData.note_type,
        content: formElements.content || currentFormData.content,
        meeting_date: formElements.meeting_date || currentFormData.meeting_date,
        // Explicitly preserve the attachment file from state
        attachment_file: currentFormData.attachment_file
      };
      console.log('Edit mode - attachment file:',
        currentFormData.attachment_file ?
        `${currentFormData.attachment_file.name} (${currentFormData.attachment_file.size} bytes)` :
        'No attachment');
    } else {
      formData = {
        ...notesFormData,
        note_type: formElements.note_type || notesFormData.note_type,
        content: formElements.content || notesFormData.content,
        meeting_date: formElements.meeting_date || notesFormData.meeting_date,
        // Explicitly preserve the attachment file from state
        attachment_file: notesFormData.attachment_file
      };
      console.log('Add mode - attachment file:',
        notesFormData.attachment_file ?
        `${notesFormData.attachment_file.name} (${notesFormData.attachment_file.size} bytes)` :
        'No attachment');
    }

    // Skip validation if editing an existing note, as values are already pre-selected
    // Just do basic sanity checks
    let validationErrors = {};

    // Always ensure we have content regardless of edit or new
    if (!formData.content || formData.content.trim() === '') {
      validationErrors.content = 'Content is required';
    }

    // Always ensure we have note_type
    if (!formData.note_type) {
      validationErrors.note_type = 'Note type is required';
    }

    // Meeting date is only required for meeting minutes type
    if (formData.note_type === 'meeting_minutes' && !formData.meeting_date) {
      validationErrors.meeting_date = 'Meeting date is required';
    }

    // File validation only when a new file is selected
    if (formData.attachment_file) {
      // File type validation if a file was selected
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(formData.attachment_file.type)) {
        validationErrors.attachment_file = 'Only image, PDF, and document files are allowed';
      }
      // File size validation (5MB limit)
      if (formData.attachment_file.size > 5 * 1024 * 1024) {
        validationErrors.attachment_file = 'File size cannot exceed 5MB';
      }
    }

    // If there are validation errors, show them and stop submission
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      // Ensure we have the correct note_type and content before submission
      console.log('Submitting form data:', {
        note_type: formData.note_type,
        content: formData.content
      });

      if (editNoteData) {
        // EDIT EXISTING NOTE
        console.log('Sending update with ID:', editNoteData.id);
        console.log('Form data before update:', formData);
        console.log('Selected note_type:', formData.note_type);
        console.log('Is note_type in formData:', 'note_type' in formData);

        // Ensure note_type is always set with explicit fallbacks
        const noteData = {
          note_type: formData.note_type || currentFormData.note_type || 'meeting_minutes',
          content: formData.content
        };

        // Add meeting_date only for meeting minutes
        if (noteData.note_type === 'meeting_minutes') {
          noteData.meeting_date = formData.meeting_date ||
            (editNoteData.meeting_date ?
              (editNoteData.meeting_date.match(/^(\d{4}-\d{2}-\d{2})/) ?
                editNoteData.meeting_date.match(/^(\d{4}-\d{2}-\d{2})/)[1] :
                new Date().toISOString().split('T')[0]) :
              new Date().toISOString().split('T')[0]);
        }

        console.log('Sending update data:', noteData);

        try {
          let updateResponse;
          // Always use FormData for consistency, whether or not we have a file
          const updateFormData = new FormData();

          // IMPORTANT: Add text fields first before the file
          // Order matters for some server implementations

          // Force string values to avoid any serialization issues
          // Make sure note_type is explicitly set and log it
          const noteType = noteData.note_type || currentFormData.note_type || 'meeting_minutes';
          console.log('Setting note_type in updateFormData:', noteType);

          updateFormData.append('note_type', String(noteType));
          updateFormData.append('content', String(noteData.content || ''));

          if (noteData.meeting_date) {
            updateFormData.append('meeting_date', String(noteData.meeting_date));
          }

          // Add single file if present (legacy support)
          if (formData.attachment_file instanceof File) {
            updateFormData.append('attachment_file', formData.attachment_file, formData.attachment_file.name);
            console.log('Adding file to update FormData:', formData.attachment_file.name,
              formData.attachment_file.size, formData.attachment_file.type);
          } else {
            console.log('No single attachment file present in formData for update');
          }

          // Add multiple files if present
          if (formData.attachments && formData.attachments.length > 0) {
            formData.attachments.forEach((file, index) => {
              updateFormData.append(`attachments[${index}]`, file, file.name);
              console.log(`Adding attachment ${index} to update FormData:`, file.name, file.size, file.type);
            });
          } else {
            console.log('No multiple attachments present in formData for update');
          }

          // Extra verification to ensure FormData is populated correctly
          console.log('FormData contents before sending:');
          for (let [key, value] of updateFormData.entries()) {
            console.log(`${key}:`, value instanceof File ?
              `File: ${value.name} (${value.size} bytes, ${value.type})` : value);
          }

          // Double check file exists in FormData
          console.log('Does FormData have attachment_file?', updateFormData.has('attachment_file'));

          // Add _method parameter for Laravel to treat as PUT request
          updateFormData.append('_method', 'PUT');          // Always send with special handling for multipart form data for consistency
          updateResponse = await disputesAPI.updateNote(dispute.id, editNoteData.id, updateFormData);

          // STEP 1: Update the local state first for immediate feedback
          console.log('STEP 1: Updating local UI state with edited note');

          const updatedNote = updateResponse.data;

          // Update main notes array
          setNotes(prevNotes => {
            const updatedNotes = prevNotes.map(note =>
              note.id === editNoteData.id ? updatedNote : note
            );
            console.log(`Updated main notes array with edited note`);
            return updatedNotes;
          });

          // Update filtered notes array based on current filter
          setFilteredNotes(prevFiltered => {
            // First check if this note is currently in the filtered list
            const noteInFiltered = prevFiltered.some(note => note.id === editNoteData.id);

            // Check if this note should be included in the current filter
            const shouldInclude =
              activeFilter === 'all' ||
              (activeFilter === 'minutes' && updatedNote.note_type === 'meeting_minutes') ||
              (activeFilter === 'case' && updatedNote.note_type === 'case_note') ||
              (activeFilter === 'resolution' && updatedNote.note_type === 'resolution_note');

            // If it's not in the filtered list but should be, add it
            if (!noteInFiltered && shouldInclude) {
              return [updatedNote, ...prevFiltered];
            }

            // If it's in the filtered list but should not be, remove it
            if (noteInFiltered && !shouldInclude) {
              return prevFiltered.filter(note => note.id !== editNoteData.id);
            }

            // Otherwise just update it in place
            if (noteInFiltered) {
              return prevFiltered.map(note =>
                note.id === editNoteData.id ? updatedNote : note
              );
            }

            return prevFiltered;
          });

          // STEP 2: Close modal and reset form
          console.log('STEP 2: Closing modal and resetting form');
          resetForm();

          // STEP 3: Fetch fresh data from database and update parent
          console.log('STEP 3: Fetching fresh data from database after note update');

          try {
            // Request fresh data from database with special flags
            if (onRefresh && typeof onRefresh === 'function') {
              console.log('Requesting fresh data from database');
              const freshData = await onRefresh({
                forceRefresh: true,
                fetchFromDatabase: true,
                operation: 'update',
                noteId: editNoteData.id
              });

              console.log('Fresh database data fetched after note update:',
                freshData && freshData.notes ? freshData.notes.length + ' notes' : 'no notes data');

              // If we got fresh data back, update our state with it
              if (freshData && freshData.notes) {
                // Sort notes by created_at date descending (newest first)
                const sortedNotes = [...freshData.notes].sort((a, b) =>
                  new Date(b.created_at) - new Date(a.created_at)
                );

                // Update both main and filtered notes arrays
                setNotes(sortedNotes);

                // Update filtered notes based on current filter
                const filtered = sortedNotes.filter(note => {
                  if (activeFilter === 'all') return true;
                  if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
                  if (activeFilter === 'case') return note.note_type === 'case_note';
                  if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
                  return true;
                });

                setFilteredNotes(filtered);
                console.log('State updated with fresh database data after note update');
              }
            } else {
              // If no refresh function available, use our helper
              console.warn('No onRefresh function available, using updateParentContainer');
              await updateParentContainer(true);
            }
          } catch (error) {
            console.error('Error fetching fresh data after note update:', error);
            // Force a local refresh as fallback
            setRefreshTrigger(prev => prev + 1);
          }          showSuccess('Note updated successfully');
        } catch (error) {
          console.error('Error updating note:', error);

          if (error.response && error.response.status === 422) {
            const backendErrors = error.response.data.errors || {};
            console.log('Backend validation errors:', backendErrors);

            // Map backend errors to our form errors
            const formErrors = {};
            Object.keys(backendErrors).forEach(key => {
              formErrors[key] = backendErrors[key][0];
            });

            // Show specific error message to the user
            const errorMessages = Object.values(formErrors).join(', ');
            showError(`Update failed: ${errorMessages}`);

            setErrors(formErrors);
            setLoading(false);
            return;
          } else {
            showError('Failed to update note. Please try again.');
            setLoading(false);
            return;
          }
        }
      } else {
        // ADD NEW NOTE
        const submitFormData = new FormData();

        // Log form data preparation
        console.log('Preparing form data from:', formData);

        // Always ensure these required fields are set explicitly as strings
        const noteType = formData.note_type || 'meeting_minutes';
        submitFormData.append('note_type', String(noteType));
        submitFormData.append('content', String(formData.content || ''));

        // Only append meeting_date for meeting minutes
        if (noteType === 'meeting_minutes') {
          submitFormData.append('meeting_date', String(formData.meeting_date || new Date().toISOString().split('T')[0]));
        }

        // Handle single file attachment (legacy support)
        if (formData.attachment_file && formData.attachment_file instanceof File) {
          submitFormData.append('attachment_file', formData.attachment_file, formData.attachment_file.name);
          console.log('Adding single attachment to form data:', formData.attachment_file.name,
            formData.attachment_file.size, formData.attachment_file.type);
        } else {
          console.log('No single attachment file present in formData for add operation');
        }

        // Handle multiple file attachments
        if (formData.attachments && formData.attachments.length > 0) {
          formData.attachments.forEach((file, index) => {
            submitFormData.append(`attachments[${index}]`, file, file.name);
            console.log(`Adding attachment ${index} to form data:`, file.name, file.size, file.type);
          });
        } else {
          console.log('No multiple attachments present in formData for add operation');
        }

        // Log the complete form data for debugging
        console.log('Form data contents before sending:');
        for (let [key, value] of submitFormData.entries()) {
          console.log(`${key}:`, value instanceof File ?
            `File: ${value.name} (${value.size} bytes, ${value.type})` : value);
        }

        // Double check file exists in FormData
        console.log('Does FormData have attachment_file?', submitFormData.has('attachment_file'));

        try {
          const addResponse = await disputesAPI.addNotes(dispute.id, submitFormData);

          console.log('Successfully added note, preparing UI updates');
          showSuccess('Notes added successfully');

          // Get the new note data
          const newNoteData = addResponse.data;

          // STEP 1: Update the local state first for immediate feedback
          // We'll update notes and filtered notes in a single batch
          console.log('STEP 1: Updating local UI state with new note');

          // Update main notes array
          setNotes(prevNotes => {
            const updatedNotes = [newNoteData, ...prevNotes];
            console.log(`Updated main notes array: ${updatedNotes.length} notes total`);
            return updatedNotes;
          });

          // Update filtered notes array
          setFilteredNotes(prevFiltered => {
            // Check if this note should be included in the current filter
            const shouldInclude =
              activeFilter === 'all' ||
              (activeFilter === 'minutes' && newNoteData.note_type === 'meeting_minutes') ||
              (activeFilter === 'case' && newNoteData.note_type === 'case_note') ||
              (activeFilter === 'resolution' && newNoteData.note_type === 'resolution_note');

            // If it should be included, add it at the beginning of the filtered list
            const updatedFiltered = shouldInclude ? [newNoteData, ...prevFiltered] : prevFiltered;
            console.log(`Updated filtered notes: ${updatedFiltered.length} notes (filter: ${activeFilter})`);
            return updatedFiltered;
          });

          // STEP 2: Close modal and reset form
          console.log('STEP 2: Closing modal and resetting form');
          resetForm();

          // STEP 3: Fetch fresh data from database after creating new note
          console.log('STEP 3: Fetching fresh data from database after note creation');

          try {
            // Request fresh data from database with special flags
            if (onRefresh && typeof onRefresh === 'function') {
              console.log('Requesting fresh data from database');
              const freshData = await onRefresh({
                forceRefresh: true,
                fetchFromDatabase: true,
                operation: 'create',
                noteId: newNoteData.id
              });

              console.log('Fresh database data fetched after note creation:',
                freshData && freshData.notes ? freshData.notes.length + ' notes' : 'no notes data');

              // If we got fresh data back, update our state with it
              if (freshData && freshData.notes) {
                // Sort notes by created_at date descending (newest first)
                const sortedNotes = [...freshData.notes].sort((a, b) =>
                  new Date(b.created_at) - new Date(a.created_at)
                );

                // Update both main and filtered notes arrays
                setNotes(sortedNotes);

                // Update filtered notes based on current filter
                const filtered = sortedNotes.filter(note => {
                  if (activeFilter === 'all') return true;
                  if (activeFilter === 'minutes') return note.note_type === 'meeting_minutes';
                  if (activeFilter === 'case') return note.note_type === 'case_note';
                  if (activeFilter === 'resolution') return note.note_type === 'resolution_note';
                  return true;
                });

                setFilteredNotes(filtered);
                console.log('State updated with fresh database data after note creation');
              }
            } else {
              // If no refresh function available, use our helper
              console.warn('No onRefresh function available, using updateParentContainer');
              await updateParentContainer(true);
            }
          } catch (error) {
            console.error('Error fetching fresh data after note creation:', error);
            // Force a local refresh as fallback
            setRefreshTrigger(prev => prev + 1);
          }
        } catch (error) {
          console.error('Error adding note:', error);

          if (error.response && error.response.status === 422) {
            const backendErrors = error.response.data.errors || {};
            console.log('Backend validation errors:', backendErrors);

            // Map backend errors to our form errors
            const formErrors = {};
            Object.keys(backendErrors).forEach(key => {
              formErrors[key] = backendErrors[key][0];
            });

            setErrors(formErrors);
            setLoading(false);
            return;
          } else {
            // Handle other errors
            showError('Failed to add note. Please try again.');
            setLoading(false);
            return;
          }
        }
      }

      // Note: We don't need these extra calls here - they're already handled in the specific
      // success flows for both edit and add operations above. Including them here creates
      // an infinite loop with multiple refreshes.

    } catch (error) {
      console.error('Error in submitNotes:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      showError('Failed to save notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 pb-4 border-b border-gray-100">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Meeting Minutes & Notes</h3>
              <p className="text-xs text-gray-500 mt-1 flex items-center">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                  {notes.length} {notes.length === 1 ? 'entry' : 'entries'}
                </span>
                <ClockIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                Last updated: {
                  notes.length > 0
                    ? new Date(notes[0].updated_at || notes[0].created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'Never'
                }
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Filter by type buttons - for easier navigation */}
            <div className="flex space-x-2 mr-2">
              <button
                type="button"
                className={`text-xs px-3 py-1.5 rounded-lg border ${
                  activeFilter === 'all'
                    ? 'border-gray-400 bg-gray-50 text-gray-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                } flex items-center transition-all duration-200`}
                onClick={() => setActiveFilter('all')}
              >
                <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                All
              </button>
              <button
                type="button"
                className={`text-xs px-3 py-1.5 rounded-lg border ${
                  activeFilter === 'minutes'
                    ? 'border-gray-400 bg-gray-50 text-gray-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                } flex items-center transition-all duration-200`}
                onClick={() => setActiveFilter('minutes')}
              >
                Minutes
              </button>
              <button
                type="button"
                className={`text-xs px-3 py-1.5 rounded-lg border ${
                  activeFilter === 'case'
                    ? 'border-gray-400 bg-gray-50 text-gray-800 '
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                } flex items-center transition-all duration-200`}
                onClick={() => setActiveFilter('case')}
              >
                Case Notes
              </button>
              <button
                type="button"
                className={`text-xs px-3 py-1.5 rounded-lg border ${
                  activeFilter === 'resolution'
                    ? 'border-gray-400 bg-gray-50 text-gray-800  shadow'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                } flex items-center transition-all duration-200`}
                onClick={() => setActiveFilter('resolution')}
              >
                Resolutions
              </button>
            </div>

            {/* Add case notes button */}
            <button
              type="button"
              onClick={() => {
                setEditNoteData(null);
                // Reset form data when opening modal for new note
                setNotesFormData({
                  note_type: 'meeting_minutes',
                  content: '',
                  meeting_date: new Date().toISOString().split('T')[0],
                  attachment_file: null
                });
                setShowAddNotesModal(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg
                        hover:bg-blue-700 shadow-md
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                        transition-all duration-200 flex items-center"
            >
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Add New Note
            </button>
          </div>
        </div>
        {contentLoading ? (
          <div className="flex justify-center items-center py-8 w-full">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500 mb-3"></div>
              <p className="text-sm text-gray-600"></p>
            </div>
          </div>
        ) : (
        <>


        {(!filteredNotes || filteredNotes.length === 0) ? (
          <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/70 flex flex-col items-center justify-center">
              <DocumentArrowDownIcon className="h-14 w-14 text-gray-400" />
            <p className="text-gray-500 text-lg font-medium mt-2">No Meeting Notes Yet</p>
            <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">No meeting minutes or notes have been recorded for this dispute case</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredNotes.map((note, index) => (
              <div
                key={note.id || index}
                className={`border border-gray-200 rounded-xl hover:shadow-md
                          transition-all duration-300 bg-white overflow-hidden h-full flex flex-col`}
                id={`note-${note.id}`}
              >
                {/* Note Header - Enhanced Card Design */}
                <div className="bg-gradient-to-r from-gray-50 to-white px-5 py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-200">
                  <div className="flex items-center mb-2 sm:mb-0">
                    <div className={`p-2.5 rounded-lg mr-3 shadow-sm ${
                      note.note_type === 'meeting_minutes' ? 'bg-violet-200' :
                      note.note_type === 'case_note' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      <CalendarIcon className={`h-5 w-5 ${
                        note.note_type === 'meeting_minutes' ? 'text-violet-600' :
                        note.note_type === 'case_note' ? 'text-blue-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div className="space-y-0.5">
                      <h4 className="text-md font-semibold text-gray-800 leading-tight">
                        {formatDate(note.meeting_date || note.created_at)}
                        {note.created_at && <span className="text-sm text-gray-500 ml-2">
                          {formatTime(note.created_at.split(' ')[1])}
                        </span>}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span className={getNoteTypeBadgeClass(note.note_type)}>
                          {getNoteTypeLabel(note.note_type)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleEditNote(note)}
                      className="p-2 rounded-lg text-gray-600 flex items-center text-xs font-medium
                               border border-gray-200 hover:bg-gray-50"
                      aria-label="Edit note"
                    >
                      <PencilIcon className="h-3.5 w-3.5 mr-1" />
                      Edit
                    </button>

                    <button
                      onClick={() => {
                        setNoteToDelete(note);
                        setShowConfirmDelete(true);
                      }}
                      className="p-2 rounded-lg text-red-600 flex items-center text-xs font-medium
                               border border-red-100 hover:bg-red-50"
                      aria-label="Delete note"
                    >
                      <TrashIcon className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </button>
                  </div>

                </div>

                {/* Note Content - Enhanced Design with View More/Less */}
                <div className="p-5">
                  <ContentWithEllipsis content={note.content} maxLength={200} />

                  {/* Legacy Single Attachment Section */}
                  {note.attachment_path && (
                    <div className="mt-4 p-4 rounded-lg border border-gray-200 ">
                      <h6 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                        <PaperClipIcon className="h-3.5 w-3.5 mr-1.5" />
                        Attachment
                      </h6>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center mb-3 sm:mb-0">
                          <div className="bg-gray-600 p-2.5 rounded-lg mr-3 shadow-md">
                            <DocumentArrowDownIcon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h5 className="text-sm font-medium text-gray-800">
                              {cleanFileName(note.attachment_path)}
                            </h5>
                            <p className="text-xs text-gray-500 mt-0.5 flex items-center">
                              <span className={`h-2 w-2 rounded-full mr-1.5 ${
                                note.attachment_path.match(/\.(jpeg|jpg|gif|png)$/i) ? 'bg-blue-400' :
                                note.attachment_path.match(/\.(pdf)$/i) ? 'bg-red-400' :
                                note.attachment_path.match(/\.(docx|doc)$/i) ? 'bg-indigo-400' :
                                note.attachment_path.match(/\.(xlsx|xls)$/i) ? 'bg-green-400' :
                                'bg-gray-400'
                              }`}></span>
                              {note.attachment_path.match(/\.(jpeg|jpg|gif|png)$/i) ? 'Image File' :
                               note.attachment_path.match(/\.(pdf)$/i) ? 'PDF Document' :
                               note.attachment_path.match(/\.(docx|doc)$/i) ? 'Word Document' :
                               note.attachment_path.match(/\.(xlsx|xls)$/i) ? 'Excel Spreadsheet' :
                               'File Attachment'}
                            </p>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewAttachment(note.file_url)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            aria-label="Preview attachment"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <a
                            href={note.file_url || '#'}
                            download={note.file_url ? cleanFileName(note.file_url) : 'attachment'}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            aria-label="Download attachment"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </a>
                          <button
                            onClick={() => handleDeleteMainAttachment(note.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            aria-label="Delete attachment"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Multiple Attachments Section */}
                  {note.attachment_urls && note.attachment_urls.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg border border-gray-200">
                      <h6 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                        <PaperClipIcon className="h-3 w-3 mr-1.5" />
                        Attachments ({note.attachment_urls.length})
                      </h6>

                      <div className="space-y-3">
                        {note.attachment_urls.map((attachment, index) => (
                          <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 bg-gray-50 rounded-md">
                            <div className="flex items-center mb-2 sm:mb-0">
                              <div className="bg-gray-600 p-2 rounded-lg mr-3">
                                <DocumentArrowDownIcon className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <h5 className="text-xs font-medium text-gray-800">
                                  {attachment.name ? cleanFileName(attachment.name) : cleanFileName(attachment.url)}
                                </h5>
                                <p className="text-xs text-gray-500 mt-0.5 flex items-center">
                                  <span className={`h-2 w-2 rounded-full mr-1.5 ${
                                    (attachment.mime && attachment.mime.includes('image')) || attachment.url.match(/\.(jpeg|jpg|gif|png)$/i) ? 'bg-blue-400' :
                                    (attachment.mime && attachment.mime.includes('pdf')) || attachment.url.match(/\.(pdf)$/i) ? 'bg-red-400' :
                                    (attachment.mime && attachment.mime.includes('word')) || attachment.url.match(/\.(docx|doc)$/i) ? 'bg-indigo-400' :
                                    (attachment.mime && attachment.mime.includes('excel')) || attachment.url.match(/\.(xlsx|xls)$/i) ? 'bg-green-400' :
                                    'bg-gray-400'
                                  }`}></span>
                                  {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : ''}
                                </p>
                              </div>
                            </div>

                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewAttachment(attachment.url)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                aria-label="Preview attachment"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <a
                                href={attachment.url}
                                download={attachment.name ? cleanFileName(attachment.name) : cleanFileName(attachment.url)}
                                className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                aria-label="Download attachment"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                              <button
                                onClick={() => handleDeleteAttachment(note.id, index)}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                aria-label="Delete attachment"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}


                </div>
              </div>
            ))}
          </div>
        )}

      {/* Confirmation Modal for Deleting Note */}
      {showConfirmDelete && noteToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl animate-fade-in-down">
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Note</h3>
              <p className="text-gray-500 mb-6">
                Are you sure you want to delete this note? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmDelete(false);
                    setNoteToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteNote(noteToDelete.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Add Notes Modal */}
      {/* File Viewer Modal */}
      <FileViewerModal
        isOpen={!!viewAttachment}
        onClose={closeAttachmentViewer}
        file={viewAttachment}
      />

      {/* Confirm Delete Attachment Modal */}
      {showConfirmDeleteAttachment && attachmentToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl animate-fade-in-down">
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Attachment</h3>
              <p className="text-gray-500 mb-6">
                Are you sure you want to delete the attachment "{cleanFileName(attachmentToDelete.name)}"? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmDeleteAttachment(false);
                    setAttachmentToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={executeAttachmentDeletion}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Add Notes Modal */}
      {showAddNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
          <div ref={modalRef} className="w-full max-w-2xl bg-white rounded-xl shadow-2xl animate-fade-in-down flex flex-col max-h-[calc(100vh-8px)] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-100 to-white-100 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-600 p-2 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold">
                    {editNoteData ? 'Edit Note' : 'Add Notes or Minutes'}
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setShowAddNotesModal(false);
                    setEditNoteData(null);
                    setErrors({});
                  }}
                  className="hover:bg-white hover:bg-opacity-20 p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all duration-200"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="text-sm text-opacity-90 mt-1">
                Case: {dispute.case_number} • {dispute.complainant_name} vs {dispute.respondent_name}
              </p>
            </div>

            <form onSubmit={submitNotes} className="flex flex-col flex-grow overflow-hidden">
              {/* Modern Form Content - Scrollable area */}
              <div className="px-6 py-5 space-y-6 overflow-y-auto flex-grow">
                {/* Note Type Selection - Radio Cards */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Note Type
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Meeting Minutes Type */}
                    <label className="cursor-pointer">
                      <div className={`border-2 rounded-lg p-2 flex flex-col items-center ${
                        (editNoteData ? currentFormData : notesFormData).note_type === 'meeting_minutes'
                          ? 'border-gray-500 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } transition-all duration-200`}>
                        <div className={`p-2 rounded-full mb-2 ${
                          (editNoteData ? currentFormData : notesFormData).note_type === 'meeting_minutes'
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <span className={`block text-sm font-medium ${
                          notesFormData.note_type === 'meeting_minutes'
                            ? 'text-gray-700'
                            : 'text-gray-700'
                        }`}>
                          Meeting Minutes
                        </span>
                        <input
                          type="radio"
                          name="note_type"
                          value="meeting_minutes"
                          checked={(editNoteData ? currentFormData : notesFormData).note_type === 'meeting_minutes'}
                          onChange={(e) => {
                            if (editNoteData) {
                              setCurrentFormData({...currentFormData, note_type: e.target.value});
                            } else {
                              setNotesFormData({...notesFormData, note_type: e.target.value});
                            }
                          }}
                          onClick={() => {
                            // Ensure note_type is explicitly set when clicked
                            if (editNoteData) {
                              setCurrentFormData(prev => ({...prev, note_type: 'meeting_minutes'}));
                            } else {
                              setNotesFormData(prev => ({...prev, note_type: 'meeting_minutes'}));
                            }
                            console.log('Clicked meeting_minutes radio');

                            // Force update a DOM data attribute for extra safety
                            document.querySelector('input[value="meeting_minutes"]').setAttribute('data-selected', 'true');
                          }}
                          className="sr-only"
                        />
                      </div>
                    </label>

                    {/* Case Note Type */}
                    <label className="cursor-pointer">
                      <div className={`border-2 rounded-lg p-2 flex flex-col items-center ${
                        (editNoteData ? currentFormData : notesFormData).note_type === 'case_note'
                          ? 'border-gray-500 bg-gray-5'
                          : 'border-gray-200 hover:border-gray-300'
                      } transition-all duration-200`}>
                        <div className={`p-2 rounded-full mb-2 ${
                          (editNoteData ? currentFormData : notesFormData).note_type === 'case_note'
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <span className={`block text-sm font-medium ${
                          (editNoteData ? currentFormData : notesFormData).note_type === 'case_note'
                            ? 'text-gray-700'
                            : 'text-gray-700'
                        }`}>
                          Case Note
                        </span>
                        <input
                          type="radio"
                          name="note_type"
                          value="case_note"
                          checked={(editNoteData ? currentFormData : notesFormData).note_type === 'case_note'}
                          onChange={(e) => {
                            if (editNoteData) {
                              setCurrentFormData({...currentFormData, note_type: e.target.value});
                            } else {
                              setNotesFormData({...notesFormData, note_type: e.target.value});
                            }
                          }}
                          onClick={() => {
                            // Ensure note_type is explicitly set when clicked
                            if (editNoteData) {
                              setCurrentFormData(prev => ({...prev, note_type: 'case_note'}));
                            } else {
                              setNotesFormData(prev => ({...prev, note_type: 'case_note'}));
                            }
                            console.log('Clicked case_note radio - setting note_type to case_note');

                            // Clear any other selected attributes
                            document.querySelectorAll('input[name="note_type"]').forEach(el => {
                              el.removeAttribute('data-selected');
                            });
                            // Set this one as selected
                            document.querySelector('input[value="case_note"]').setAttribute('data-selected', 'true');
                          }}
                          className="sr-only"
                        />
                      </div>
                    </label>

                    {/* Resolution Note Type */}
                    <label className="cursor-pointer">
                      <div className={`border-2 rounded-lg p-2 flex flex-col items-center ${
                        (editNoteData ? currentFormData : notesFormData).note_type === 'resolution_note'
                          ? 'border-gray-500 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } transition-all duration-200`}>
                        <div className={`p-2 rounded-full mb-2 ${
                          (editNoteData ? currentFormData : notesFormData).note_type === 'resolution_note'
                            ? 'bg-gray-200 text-gray-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className={`block text-sm font-medium ${
                          (editNoteData ? currentFormData : notesFormData).note_type === 'resolution_note'
                            ? 'text-gray-700'
                            : 'text-gray-700'
                        }`}>
                          Resolution Note
                        </span>
                        <input
                          type="radio"
                          name="note_type"
                          value="resolution_note"
                          checked={(editNoteData ? currentFormData : notesFormData).note_type === 'resolution_note'}
                          onChange={(e) => {
                            if (editNoteData) {
                              setCurrentFormData({...currentFormData, note_type: e.target.value});
                            } else {
                              setNotesFormData({...notesFormData, note_type: e.target.value});
                            }
                          }}
                          onClick={() => {
                            // Ensure note_type is explicitly set when clicked
                            if (editNoteData) {
                              setCurrentFormData(prev => ({...prev, note_type: 'resolution_note'}));
                            } else {
                              setNotesFormData(prev => ({...prev, note_type: 'resolution_note'}));
                            }
                            console.log('Clicked resolution_note radio - setting note_type to resolution_note');

                            // Clear any other selected attributes
                            document.querySelectorAll('input[name="note_type"]').forEach(el => {
                              el.removeAttribute('data-selected');
                            });
                            // Set this one as selected
                            document.querySelector('input[value="resolution_note"]').setAttribute('data-selected', 'true');
                          }}
                          className="sr-only"
                        />
                      </div>
                    </label>
                  </div>
                  {errors.note_type && <p className="mt-1 text-sm text-red-600">{errors.note_type}</p>}
                </div>

                {/* Meeting Date (conditional) */}
                {notesFormData.note_type === 'meeting_minutes' && (
                  <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
                    <label htmlFor="meeting_date" className="flex items-center text-gray-700 font-medium mb-2">
                      <CalendarIcon className="h-5 w-5 text-gray-600 mr-2" />
                      <span>Meeting Date</span>
                    </label>
                    <input
                      type="date"
                      name="meeting_date"
                      id="meeting_date"
                      value={notesFormData.meeting_date}
                      onChange={(e) => setNotesFormData({...notesFormData, meeting_date: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                    {errors.meeting_date && <p className="mt-1 text-sm text-red-600">{errors.meeting_date}</p>}
                  </div>
                )}

                {/* Content */}
                <div>
                  <label htmlFor="content" className="flex items-center text-gray-700 font-medium mb-2">
                    <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <span>Content</span>
                  </label>
                  <div className="relative">
                    <textarea
                      id="content"
                      name="content"
                      rows={(editNoteData ? currentFormData : notesFormData).note_type === 'meeting_minutes' ? 6 : 4}
                      value={editNoteData ? currentFormData.content : notesFormData.content}
                      onChange={(e) => {
                        if (editNoteData) {
                          setCurrentFormData({...currentFormData, content: e.target.value});
                        } else {
                          setNotesFormData({...notesFormData, content: e.target.value});
                        }
                      }}
                      placeholder={(editNoteData ? currentFormData : notesFormData).note_type === 'meeting_minutes'
                        ? 'Enter the minutes of the meeting...'
                        : notesFormData.note_type === 'resolution_note'
                          ? 'Enter resolution details...'
                          : 'Enter your case notes...'}
                      className={` ${
                        errors.content ? 'border-red-500' : 'border-gray-300'
                      } w-full border border-gray-300 rounded-lg px-3 py-2`}
                      required
                    />
                    {errors.content && <p className="mt-1 text-sm text-red-600">{errors.content}</p>}
                  </div>
                </div>

                {/* Modern File Upload Area */}
                <div>
                  <label className="flex items-center text-gray-700 font-medium mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span>Attachment (Optional)</span>
                    {editNoteData && editNoteData.attachment_path && (
                      <span className="text-xs text-gray-500 ml-2">
                        (Leave empty to keep current attachment)
                      </span>
                    )}
                  </label>

                  {/* Show upload area if:
                      1. In Add Mode (no editNoteData): No file selected yet
                      2. In Edit Mode: No new file selected AND no existing attachment
                  */}
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 bg-gray-50/30 text-center hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex flex-col md:flex-row justify-center gap-4">
                      {/* Single File Upload Option */}
                      <div className="flex-1 md:border-r md:border-gray-200 md:pr-4">
                        <input
                          type="file"
                          name="attachment_file"
                          id="attachment_file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          className="hidden"
                          accept="image/jpeg,image/png,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        />
                        <label htmlFor="attachment_file" className="cursor-pointer flex flex-col items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-sm font-medium text-gray-600 mb-1">Upload Single File</span>
                          <span className="text-xs text-gray-500">PDF, DOC, JPG, PNG up to 5MB</span>
                        </label>
                      </div>

                      {/* Multiple File Upload Option */}
                      <div className="flex-1 md:pl-4 mt-4 md:mt-0">
                        <input
                          type="file"
                          name="attachments"
                          id="attachments"
                          ref={multipleFileInputRef}
                          onChange={handleMultipleFileChange}
                          className="hidden"
                          multiple
                          accept="image/jpeg,image/png,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        />
                        <label htmlFor="attachments" className="cursor-pointer flex flex-col items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-600 mb-1">Upload Multiple Files</span>
                          <span className="text-xs text-gray-500">Select up to 10 files</span>
                        </label>
                      </div>
                    </div>
                  </div>


                  {/* Display selected single attachment */}
                  {(editNoteData ? currentFormData.attachment_file : notesFormData.attachment_file) && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-white rounded-md shadow-sm">
                            <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                          </div>
                          <div>
                            {editNoteData ? (
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {currentFormData.attachment_file instanceof File
                                  ? cleanFileName(currentFormData.attachment_file.name)
                                  : (currentFormData.attachment_url
                                    ? cleanFileName(currentFormData.attachment_url)
                                    : 'Current Attachment')}
                              </p>
                            ) : (
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {cleanFileName(notesFormData.attachment_file.name)}
                              </p>
                            )}
                            {(editNoteData && currentFormData.attachment_file instanceof File) ||
                             (!editNoteData && notesFormData.attachment_file) ? (
                              <p className="text-xs text-gray-500">
                                {((editNoteData ? currentFormData.attachment_file.size : notesFormData.attachment_file.size) / 1024).toFixed(1)} KB
                              </p>
                            ) : (
                              <p className="text-xs text-gray-500">Current attachment</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (editNoteData) {
                              setCurrentFormData({...currentFormData, attachment_file: null, attachment_url: null});
                            } else {
                              setNotesFormData({...notesFormData, attachment_file: null});
                            }
                          }}
                          className="p-1.5 bg-white text-red-500 hover:bg-red-50 rounded-md transition-colors duration-200"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Display selected multiple attachments */}
                  {((editNoteData && currentFormData.attachments && currentFormData.attachments.length > 0) ||
                    (!editNoteData && notesFormData.attachments && notesFormData.attachments.length > 0)) && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files ({editNoteData ? currentFormData.attachments.length : notesFormData.attachments.length})</h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                        {(editNoteData ? currentFormData.attachments : notesFormData.attachments).map((file, index) => (
                          <div key={index} className={`flex justify-between items-center p-3 ${index !== 0 ? 'border-t border-gray-200' : ''}`}>
                            <div className="flex items-center space-x-3">
                              <div className="p-1.5 bg-white rounded-md shadow-sm">
                                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800 truncate">{cleanFileName(file.name)}</p>
                                <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (editNoteData) {
                                  const newAttachments = [...currentFormData.attachments];
                                  newAttachments.splice(index, 1);
                                  setCurrentFormData({...currentFormData, attachments: newAttachments});
                                } else {
                                  const newAttachments = [...notesFormData.attachments];
                                  newAttachments.splice(index, 1);
                                  setNotesFormData({...notesFormData, attachments: newAttachments});
                                }
                              }}
                              className="p-1.5 bg-white text-red-500 hover:bg-red-50 rounded-md transition-colors duration-200"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show error messages for both attachment types */}
                  {errors.attachment_file && <p className="mt-1 text-sm text-red-600">{errors.attachment_file}</p>}
                  {errors.attachments && <p className="mt-1 text-sm text-red-600">{errors.attachments}</p>}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3 border-t border-gray-200 sticky bottom-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddNotesModal(false);
                    setEditNoteData(null);
                    setErrors({});
                  }}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm
                           focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 transition-all duration-200"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700
                           focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500
                           transition-all duration-200 shadow-md flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      {editNoteData ? 'Update Note' : 'Save Notes'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default React.memo(MinutesTabContent);
